/**
 * EXAMPLE USAGE: How to generate dynamic LLM prompts for scenario validation/generation
 *
 * This file contains utilities and examples for working with LLM-based scenario generation.
 *
 * ## Core Utilities:
 *
 * ### Patch Application:
 * - `applyPatchesToScenario(scenario, patches)` - Apply LLM-generated patches to a scenario
 * - `validateScenarioStructure(scenario)` - Validate scenario has proper graph structure
 * - `repairScenarioBidirectionalLinks(scenario, options)` - Auto-repair broken bidirectional links
 * - `repairBrokenScenario(scenario)` - Validate + repair in one step (standalone)
 *
 * ### Response Parsing:
 * - `parseScenarioFromLLMResponse(responseText)` - Parse payload mode responses
 * - `parsePatchesFromLLMResponse(responseText)` - Parse patch mode responses (with fallback)
 * - `validatePatchResponse(responseText)` - Check if response has proper markers
 * - `checkResponseFormat(responseText, mode)` - Quick format validation
 *
 * ### LLM Integration:
 * - `generatePatchesWithRetry(...)` - Call LLM with automatic retry on format errors
 * - `generateScenarioPatchesWithRetry(...)` - Generate patches with proper error handling
 * - `modifyScenarioWithLLM(...)` - Complete workflow: generate patches + apply + validate
 *
 * ## Quick Start Examples:
 *
 * ### Example 1: Modify a scenario using LLM (RECOMMENDED)
 * ```javascript
 * import { modifyScenarioWithLLM } from './generateLLMPrompt.example';
 *
 * const result = await modifyScenarioWithLLM(
 *   "Add a welcome message at the beginning",
 *   currentScenario,
 *   tasks,
 *   apiKey
 * );
 *
 * if (result.success) {
 *   console.log('Modified scenario:', result.scenario);
 *   console.log('Changes:', result.changes);
 * }
 * ```
 *
 * ### Example 2: Apply patches manually (no LLM)
 * ```javascript
 * import { applyPatchesToScenario } from './generateLLMPrompt.example';
 *
 * const patches = {
 *   add: { "new-id": { ...nodeData } },
 *   update: { "existing-id": { set: { "data.message": "Updated" } } },
 *   remove: ["old-id"]
 * };
 *
 * const result = applyPatchesToScenario(currentScenario, patches);
 * console.log('Modified:', result.scenario);
 * ```
 *
 * ### Example 3: React Hook
 * ```javascript
 * import { useScenarioLLMModification } from './generateLLMPrompt.example';
 *
 * function MyComponent() {
 *   const { modifyScenario, loading, error } = useScenarioLLMModification(apiKey);
 *
 *   const handleModify = async () => {
 *     const result = await modifyScenario(
 *       "Add a question node",
 *       currentScenario,
 *       tasks
 *     );
 *   };
 * }
 * ```
 *
 * ### Example 4: Repair broken scenario links
 * ```javascript
 * import { repairBrokenScenario } from './generateLLMPrompt.example';
 *
 * const result = repairBrokenScenario(brokenScenario);
 * if (result.isValidNow) {
 *   console.log('Fixed!', result.scenario);
 *   console.log('Applied repairs:', result.repairs);
 * }
 * ```
 */

import { generateRuntimePrompt } from './generateLLMPrompt';
import { nodeDefinitions } from '../configs/default-items-definition';
import { nodeItemsConfig } from '../configs/default-items-config';
import { testScenario } from '../mocks/scenario.mock';
import React from 'react';

/**
 * Utility: Set a value at a dot-path in an object
 * Example: setValueAtPath(obj, "data.message", "Hello") sets obj.data.message = "Hello"
 */
function setValueAtPath(obj, path, value) {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

/**
 * Utility: Remove a node and clean up all references to it in other nodes
 */
function removeNodeAndCleanReferences(scenario, nodeIdToRemove) {
  // Remove the node itself
  delete scenario[nodeIdToRemove];

  // Clean up references in other nodes
  Object.values(scenario).forEach(node => {
    // Skip null or undefined nodes
    if (!node || typeof node !== 'object') {
      return;
    }

    if (node.nextNodes) {
      node.nextNodes = node.nextNodes.filter(id => id !== nodeIdToRemove);
    }
    if (node.prevNodes) {
      node.prevNodes = node.prevNodes.filter(id => id !== nodeIdToRemove);
    }
  });
}

/**
 * Utility: Apply patches to a scenario and return the modified scenario
 * @param {Object} scenario - The current scenario object
 * @param {Object} patches - The patches object with add/update/remove operations
 * @returns {Object} - The modified scenario (new object, does not mutate original)
 */
export function applyPatchesToScenario(scenario, patches) {
  // Validate inputs
  if (!scenario || typeof scenario !== 'object') {
    console.error('Invalid scenario provided to applyPatchesToScenario:', scenario);
    return {
      success: false,
      scenario: scenario || {},
      changes: {
        added: [],
        updated: [],
        removed: [],
        errors: ['Invalid or null scenario provided'],
      },
    };
  }

  if (!patches || typeof patches !== 'object') {
    console.warn('No valid patches provided');
    return {
      success: true,
      scenario,
      changes: {
        added: [],
        updated: [],
        removed: [],
        errors: [],
      },
    };
  }

  // Deep clone the scenario to avoid mutating the original
  const modifiedScenario = JSON.parse(JSON.stringify(scenario));

  // Track changes for logging
  const changes = {
    added: [],
    updated: [],
    removed: [],
    errors: [],
    warnings: [], // Non-critical issues (e.g., trying to update already-removed nodes)
  };

  // 1. Handle removals first (to avoid updating nodes that will be deleted)
  if (patches.remove && Array.isArray(patches.remove)) {
    patches.remove.forEach(nodeId => {
      if (modifiedScenario[nodeId]) {
        removeNodeAndCleanReferences(modifiedScenario, nodeId);
        changes.removed.push(nodeId);
        console.log(`✅ Removed node: ${nodeId}`);
      } else {
        const error = `Node ${nodeId} not found (cannot remove)`;
        changes.errors.push(error);
        console.warn(`⚠️ ${error}`);
      }
    });
  }

  // 2. Handle additions
  if (patches.add && typeof patches.add === 'object') {
    Object.entries(patches.add).forEach(([nodeId, nodeData]) => {
      // Check if node exists and is not null
      const existingNode = modifiedScenario[nodeId];
      if (existingNode !== undefined && existingNode !== null) {
        // Replace the existing node instead of erroring
        console.warn(`⚠️ Node ${nodeId} already exists, replacing it`);
        modifiedScenario[nodeId] = nodeData;
        changes.updated.push(nodeId);
        console.log(`✅ Replaced existing node: ${nodeId}`);
      } else {
        // Add the new node
        modifiedScenario[nodeId] = nodeData;
        changes.added.push(nodeId);
        console.log(`✅ Added node: ${nodeId}`);
      }
    });
  }

  // 3. Handle updates
  if (patches.update && typeof patches.update === 'object') {
    Object.entries(patches.update).forEach(([nodeId, updates]) => {
      const node = modifiedScenario[nodeId];

      if (!node || typeof node !== 'object') {
        // This is common when removing multiple sequential nodes - adjacent nodes may already be removed
        // Treat as warning, not error
        const warning = `Node ${nodeId} not found or invalid (cannot update) - likely already removed`;
        changes.warnings.push(warning);
        console.warn(`⚠️ ${warning}`);
        return;
      }

      // Handle dot-path field updates
      if (updates.set && typeof updates.set === 'object') {
        Object.entries(updates.set).forEach(([path, value]) => {
          setValueAtPath(node, path, value);
          console.log(`✅ Updated ${nodeId}.${path}`);
        });
      }

      // Handle link updates (nextNodes/prevNodes)
      if (updates.links && typeof updates.links === 'object') {
        if (updates.links.nextNodes) {
          node.nextNodes = updates.links.nextNodes;
          console.log(`✅ Updated ${nodeId}.nextNodes`);
        }
        if (updates.links.prevNodes) {
          node.prevNodes = updates.links.prevNodes;
          console.log(`✅ Updated ${nodeId}.prevNodes`);
        }
      }

      changes.updated.push(nodeId);
    });
  }

  console.log('Patch application summary:', {
    added: changes.added.length,
    updated: changes.updated.length,
    removed: changes.removed.length,
    errors: changes.errors.length,
    warnings: changes.warnings.length,
  });

  if (changes.errors.length > 0) {
    console.warn('⚠️ Patch application completed with errors:', changes.errors);
  }

  if (changes.warnings.length > 0) {
    console.warn('⚠️ Patch application warnings (non-critical):', changes.warnings);
  }

  return {
    scenario: modifiedScenario,
    changes,
    success: changes.errors.length === 0, // Only errors count as failures, not warnings
  };
}

/**
 * Utility: Validate that a scenario has proper structure after patching
 */
export function validateScenarioStructure(scenario) {
  const errors = [];
  const warnings = [];

  // Check for start and finish nodes
  const nodes = Object.values(scenario);
  const startNodes = nodes.filter(n => n.type === 'StartNode');
  const finishNodes = nodes.filter(n => n.type === 'FinishNode');

  if (startNodes.length === 0) {
    errors.push('Missing StartNode');
  } else if (startNodes.length > 1) {
    errors.push('Multiple StartNodes found');
  }

  if (finishNodes.length === 0) {
    errors.push('Missing FinishNode');
  } else if (finishNodes.length > 1) {
    errors.push('Multiple FinishNodes found');
  }

  // Check bidirectional links
  Object.entries(scenario).forEach(([nodeId, node]) => {
    // Check nextNodes references exist and have back-references
    if (node.nextNodes) {
      node.nextNodes.forEach(nextId => {
        const nextNode = scenario[nextId];
        if (!nextNode) {
          errors.push(`Node ${nodeId} references non-existent nextNode ${nextId}`);
        } else if (!nextNode.prevNodes || !nextNode.prevNodes.includes(nodeId)) {
          errors.push(`Node ${nodeId} → ${nextId} missing back-reference (prevNodes)`);
        }
      });
    }

    // Check prevNodes references exist and have forward-references
    if (node.prevNodes) {
      node.prevNodes.forEach(prevId => {
        const prevNode = scenario[prevId];
        if (!prevNode) {
          errors.push(`Node ${nodeId} references non-existent prevNode ${prevId}`);
        } else if (!prevNode.nextNodes || !prevNode.nextNodes.includes(nodeId)) {
          errors.push(`Node ${nodeId} ← ${prevId} missing forward-reference (nextNodes)`);
        }
      });
    }

    // Check for orphaned nodes (except StartNode)
    if (node.type !== 'StartNode') {
      if (!node.prevNodes || node.prevNodes.length === 0) {
        warnings.push(`Node ${nodeId} has no prevNodes (orphaned)`);
      }
    }

    // Check for dead-end nodes (except FinishNode)
    if (node.type !== 'FinishNode') {
      if (!node.nextNodes || node.nextNodes.length === 0) {
        warnings.push(`Node ${nodeId} has no nextNodes (dead end)`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Utility: Auto-repair broken bidirectional links in a scenario
 * This function fixes scenarios where links are inconsistent between nextNodes and prevNodes
 *
 * @param {Object} scenario - The scenario with potentially broken links
 * @param {Object} options - Repair options
 * @param {boolean} options.removeOrphans - Remove nodes that can't be reached from StartNode
 * @param {boolean} options.removeDeadEnds - Remove nodes that don't lead to FinishNode (except special nodes)
 * @returns {Object} - { scenario: repairedScenario, repairs: [...], removed: [...] }
 */
export function repairScenarioBidirectionalLinks(scenario, options = {}) {
  const { removeOrphans = false, removeDeadEnds = false } = options;

  // Deep clone to avoid mutating original
  const repairedScenario = JSON.parse(JSON.stringify(scenario));
  const repairs = [];
  const removed = [];

  // Step 1: Build a complete map of all relationships
  const forwardLinks = {}; // nodeId -> Set of nextNode ids
  const backwardLinks = {}; // nodeId -> Set of prevNode ids

  Object.entries(repairedScenario).forEach(([nodeId, node]) => {
    forwardLinks[nodeId] = new Set(node.nextNodes || []);
    backwardLinks[nodeId] = new Set(node.prevNodes || []);
  });

  // Step 2: Synchronize bidirectional links
  // For each forward link A → B, ensure B has backward link B ← A
  Object.entries(repairedScenario).forEach(([nodeId, node]) => {
    if (node.nextNodes) {
      node.nextNodes.forEach(nextId => {
        if (repairedScenario[nextId]) {
          if (!backwardLinks[nextId].has(nodeId)) {
            backwardLinks[nextId].add(nodeId);
            repairs.push(`Added missing prevNode: ${nextId} ← ${nodeId}`);
            console.log(`🔧 Repair: Added ${nodeId} to ${nextId}.prevNodes`);
          }
        } else {
          // Next node doesn't exist - remove the reference
          forwardLinks[nodeId].delete(nextId);
          repairs.push(`Removed reference to non-existent node: ${nodeId} → ${nextId}`);
          console.log(`🔧 Repair: Removed invalid nextNode ${nextId} from ${nodeId}`);
        }
      });
    }

    if (node.prevNodes) {
      node.prevNodes.forEach(prevId => {
        if (repairedScenario[prevId]) {
          if (!forwardLinks[prevId].has(nodeId)) {
            forwardLinks[prevId].add(nodeId);
            repairs.push(`Added missing nextNode: ${prevId} → ${nodeId}`);
            console.log(`🔧 Repair: Added ${nodeId} to ${prevId}.nextNodes`);
          }
        } else {
          // Prev node doesn't exist - remove the reference
          backwardLinks[nodeId].delete(prevId);
          repairs.push(`Removed reference to non-existent node: ${nodeId} ← ${prevId}`);
          console.log(`🔧 Repair: Removed invalid prevNode ${prevId} from ${nodeId}`);
        }
      });
    }
  });

  // Step 3: Apply the synchronized links back to the scenario
  Object.keys(repairedScenario).forEach(nodeId => {
    repairedScenario[nodeId].nextNodes = Array.from(forwardLinks[nodeId] || []);
    repairedScenario[nodeId].prevNodes = Array.from(backwardLinks[nodeId] || []);
  });

  // Step 4: Optional - Remove orphaned nodes
  if (removeOrphans) {
    const startNodes = Object.values(repairedScenario).filter(n => n.type === 'StartNode');
    if (startNodes.length === 1) {
      const reachableNodes = new Set();
      const queue = [startNodes[0].id];

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (reachableNodes.has(nodeId)) continue;
        reachableNodes.add(nodeId);

        const node = repairedScenario[nodeId];
        if (node && node.nextNodes) {
          node.nextNodes.forEach(nextId => {
            if (!reachableNodes.has(nextId)) {
              queue.push(nextId);
            }
          });
        }
      }

      // Remove unreachable nodes
      Object.keys(repairedScenario).forEach(nodeId => {
        if (!reachableNodes.has(nodeId)) {
          delete repairedScenario[nodeId];
          removed.push(nodeId);
          console.log(`🗑️ Removed orphaned node: ${nodeId}`);
        }
      });
    }
  }

  // Step 5: Optional - Remove dead-end nodes (that don't lead to FinishNode)
  if (removeDeadEnds) {
    const finishNodes = Object.values(repairedScenario).filter(n => n.type === 'FinishNode');
    if (finishNodes.length === 1) {
      const pathToFinish = new Set();
      const queue = [finishNodes[0].id];

      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (pathToFinish.has(nodeId)) continue;
        pathToFinish.add(nodeId);

        const node = repairedScenario[nodeId];
        if (node && node.prevNodes) {
          node.prevNodes.forEach(prevId => {
            if (!pathToFinish.has(prevId)) {
              queue.push(prevId);
            }
          });
        }
      }

      // Remove nodes that don't lead to finish
      Object.keys(repairedScenario).forEach(nodeId => {
        const node = repairedScenario[nodeId];
        // Don't remove IfClose or IfCondition nodes as they might be structural
        if (!pathToFinish.has(nodeId) &&
            node.type !== 'IfCloseNode' &&
            node.type !== 'IfConditionNode') {
          delete repairedScenario[nodeId];
          removed.push(nodeId);
          console.log(`🗑️ Removed dead-end node: ${nodeId}`);
        }
      });
    }
  }

  console.log(`✅ Repair complete: ${repairs.length} fixes, ${removed.length} nodes removed`);

  return {
    scenario: repairedScenario,
    repairs,
    removed,
    repairCount: repairs.length,
  };
}

/**
 * Utility: Parse LLM response with special markers
 */
export function parseScenarioFromLLMResponse(responseText) {
  const payloadStart = responseText.indexOf('<<<SCENARIO_PAYLOAD>>>');
  const payloadEnd = responseText.indexOf('<<<END_PAYLOAD>>>');

  if (payloadStart === -1 || payloadEnd === -1) {
    // No payload markers found - this is a text-only response
    return { explanation: responseText, payload: null };
  }

  const explanation = responseText.substring(0, payloadStart).trim();
  const payloadJson = responseText.substring(
    payloadStart + '<<<SCENARIO_PAYLOAD>>>'.length,
    payloadEnd
  ).trim();
  const notes = responseText.substring(payloadEnd + '<<<END_PAYLOAD>>>'.length).trim();

  try {
    const payload = JSON.parse(payloadJson);
    return { explanation, payload, notes };
  } catch (error) {
    console.error('Failed to parse payload JSON:', error);
    return { explanation, payload: null, notes, error };
  }
}

/**
 * Utility: Parse LLM response when using patch mode
 */
export function parsePatchesFromLLMResponse(responseText) {
  const startMarker = '<<<SCENARIO_PATCHES>>>';
  const endMarker = '<<<END_PATCHES>>>';

  const payloadStart = responseText.indexOf(startMarker);
  const payloadEnd = responseText.indexOf(endMarker);

  if (payloadStart === -1 || payloadEnd === -1) {
    // No patch markers found - this is a text-only response or the LLM didn't follow format
    console.warn('⚠️ LLM Response missing required markers!');
    console.warn('Expected markers:', startMarker, 'and', endMarker);
    console.warn('Response preview:', responseText.substring(0, 200));

    // Try to find JSON object as fallback
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      console.warn('⚠️ Found JSON without markers - attempting to parse as fallback');
      try {
        const patches = JSON.parse(jsonMatch[0]);
        return {
          explanation: responseText.substring(0, jsonMatch.index).trim(),
          patches,
          notes: responseText.substring(jsonMatch.index + jsonMatch[0].length).trim(),
          warning: 'LLM did not use required markers. Please check prompt configuration.'
        };
      } catch (error) {
        console.error('Failed to parse fallback JSON:', error);
      }
    }

    return {
      explanation: responseText,
      patches: null,
      error: 'Response missing required <<<SCENARIO_PATCHES>>> and <<<END_PATCHES>>> markers'
    };
  }

  const explanation = responseText.substring(0, payloadStart).trim();
  let payloadJson = responseText.substring(
    payloadStart + startMarker.length,
    payloadEnd
  ).trim();
  const notes = responseText.substring(payloadEnd + endMarker.length).trim();

  // Remove markdown code fences if present (common LLM mistake)
  if (payloadJson.startsWith('```json')) {
    console.warn('⚠️ LLM included markdown code fences - removing them');
    payloadJson = payloadJson.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (payloadJson.startsWith('```')) {
    console.warn('⚠️ LLM included markdown code fences - removing them');
    payloadJson = payloadJson.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }

  try {
    const patches = JSON.parse(payloadJson);

    // Validate patch structure
    const validKeys = ['add', 'update', 'remove'];
    const patchKeys = Object.keys(patches);
    const invalidKeys = patchKeys.filter(key => !validKeys.includes(key));

    if (invalidKeys.length > 0) {
      console.warn('⚠️ Patch contains unexpected keys:', invalidKeys);
    }

    return { explanation, patches, notes };
  } catch (error) {
    console.error('Failed to parse patches JSON:', error);
    console.error('Invalid JSON content:', payloadJson.substring(0, 200));
    return {
      explanation,
      patches: null,
      notes,
      error: `JSON parsing failed: ${error.message}`,
      invalidJson: payloadJson
    };
  }
}

/**
 * Utility: Validate that LLM response follows the required format
 */
export function validatePatchResponse(responseText) {
  const hasStartMarker = responseText.includes('<<<SCENARIO_PATCHES>>>');
  const hasEndMarker = responseText.includes('<<<END_PATCHES>>>');
  const hasBothMarkers = hasStartMarker && hasEndMarker;

  // Check for common formatting mistakes
  const hasCodeFences = responseText.includes('```json') || responseText.includes('```');
  const hasWrongMarkers = responseText.includes('<<<PATCHES>>>') || responseText.includes('<<<SCENARIO_PAYLOAD>>>');

  return {
    isValid: hasBothMarkers && !hasCodeFences && !hasWrongMarkers,
    hasStartMarker,
    hasEndMarker,
    hasBothMarkers,
    issues: [
      !hasStartMarker && 'Missing <<<SCENARIO_PATCHES>>> marker',
      !hasEndMarker && 'Missing <<<END_PATCHES>>> marker',
      hasCodeFences && 'Contains markdown code fences (```)',
      hasWrongMarkers && 'Using incorrect marker names',
    ].filter(Boolean),
  };
}

/**
 * Utility: Retry LLM call with enhanced prompt when format validation fails
 */
export async function generatePatchesWithRetry(
  systemPrompt,
  userRequest,
  apiKey,
  maxRetries = 2
) {
  let lastResponse = null;
  let lastError = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 4096,
          system: systemPrompt,
          messages: [
            ...(lastResponse ? [{
              role: 'assistant',
              content: lastResponse
            }, {
              role: 'user',
              content: `⚠️ Your previous response did not follow the required format. You MUST use the <<<SCENARIO_PATCHES>>> and <<<END_PATCHES>>> markers exactly as shown in the system prompt. Please provide the response again with the correct format.`
            }] : []),
            {
              role: 'user',
              content: userRequest,
            },
          ],
        }),
      });

      const result = await response.json();
      const responseText = result.content[0].text;
      lastResponse = responseText;

      // Validate the response format
      const validation = validatePatchResponse(responseText);

      if (validation.isValid) {
        // Parse and return the patches
        const parsed = parsePatchesFromLLMResponse(responseText);
        return {
          success: true,
          ...parsed,
          attempts: attempt + 1,
        };
      } else {
        console.warn(`⚠️ Attempt ${attempt + 1} failed validation:`, validation.issues);
        lastError = validation.issues.join(', ');

        // If this is the last attempt, try to parse anyway with fallback
        if (attempt === maxRetries - 1) {
          const parsed = parsePatchesFromLLMResponse(responseText);
          return {
            success: !!parsed.patches,
            ...parsed,
            attempts: attempt + 1,
            validationWarning: `Response format issues: ${lastError}`,
          };
        }
      }
    } catch (error) {
      console.error(`⚠️ Attempt ${attempt + 1} failed:`, error);
      lastError = error.message;

      if (attempt === maxRetries - 1) {
        return {
          success: false,
          error: `All retry attempts failed. Last error: ${lastError}`,
          attempts: attempt + 1,
        };
      }
    }
  }
}

/**
 * Example 1: Generate prompt with current system configuration
 */
export function generatePromptForCurrentSystem(tasks) {
  // Fetch available tasks from your API or state
  const availableTasks = tasks || [
    {
      id: 'yoga-123',
      title: 'Yoga',
      backgroundColor: 'hsla(152, 74%, 79%)',
      avatarColor: 'hsla(151, 61%, 29%)',
      avatar: '<svg>...</svg>',
    },
    {
      id: 'run-456',
      title: 'Run',
      backgroundColor: 'hsla(30, 82%, 85%)',
      avatarColor: 'hsla(29, 62%, 26%)',
      avatar: '<svg>...</svg>',
    },
  ];

  // Generate the complete prompt
  const promptForLLM = generateRuntimePrompt(
    availableTasks,
    nodeDefinitions,
    nodeItemsConfig,
    testScenario // Optional: include example scenario
  );

  return promptForLLM;
}

/**
 * Example 2: Use in API call to LLM for scenario generation
 */
export async function generateScenarioWithLLM(userRequest, tasks) {
  const systemPrompt = generateRuntimePrompt(
    tasks,
    nodeDefinitions,
    nodeItemsConfig,
    testScenario
  );

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.ANTHROPIC_API_KEY,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: 'claude-3-5-sonnet-20241022',
      max_tokens: 4096,
      system: systemPrompt, // Use the dynamic prompt as system instructions
      messages: [
        {
          role: 'user',
          content: userRequest, // e.g., "Create a wellness scenario with yoga and meditation tasks"
        },
      ],
    }),
  });

  const result = await response.json();
  const responseText = result.content[0].text;

  // Parse the response using the marker-based parser
  const { explanation, payload, notes } = parseScenarioFromLLMResponse(responseText);

  return {
    explanation,  // LLM's explanation of what it did
    scenario: payload,  // The actual scenario JSON (or null if not provided)
    notes,  // Any additional notes from the LLM
    rawResponse: responseText,  // Full response for debugging
  };
}

/**
 * Example 3: Validate existing scenario with LLM
 */
export async function validateScenarioWithLLM(scenarioPayload, tasks) {
  const systemPrompt = generateRuntimePrompt(
    tasks,
    nodeDefinitions,
    nodeItemsConfig
  );

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.ANTHROPIC_API_KEY,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: 'claude-3-5-sonnet-20241022',
      max_tokens: 2048,
      system: systemPrompt,
      messages: [
        {
          role: 'user',
          content: `Validate this scenario and report any errors. If there are errors, provide a corrected version:\n\n${JSON.stringify(scenarioPayload, null, 2)}`,
        },
      ],
    }),
  });

  const result = await response.json();
  const responseText = result.content[0].text;

  // Parse the response - validation might include a corrected payload
  const { explanation, payload, notes } = parseScenarioFromLLMResponse(responseText);

  return {
    validationReport: explanation,  // LLM's validation findings
    correctedScenario: payload,  // Fixed scenario if errors were found (or null)
    additionalNotes: notes,
    hasErrors: payload !== null,  // If payload provided, errors were found and fixed
  };
}

/**
 * Example 4: Use with useScenarioConfig hook
 */
export function useScenarioLLMPrompt(tasks, itemType = 'scenario') {
  const [prompt, setPrompt] = React.useState('');

  React.useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    // Generate normalized config (same logic as useScenarioConfig)
    const configTasksList = tasks.map((task) => ({
      id: task.id,
      title: task.title,
      nodeType: `task:${task.id}`,
      backgroundColor: task.backgroundColor,
      avatarColor: task.avatarColor,
    }));

    // For non-surveyTask, update nodeDefinitions with task enums
    let updatedDefinitions = nodeDefinitions;
    if (itemType !== 'surveyTask') {
      const nodeDefinitionsDeepCopy = JSON.parse(JSON.stringify(nodeDefinitions));

      nodeDefinitionsDeepCopy['task:*'].validationSchema.properties.nodeType.enum =
        configTasksList.map((task) => task.nodeType);

      nodeDefinitionsDeepCopy['task:*'].nodeInfoContent[0].options = configTasksList.map(
        (task) => ({ label: task.title, value: task.nodeType })
      );

      updatedDefinitions = nodeDefinitionsDeepCopy;
    }

    // Generate prompt with updated definitions
    const generatedPrompt = generateRuntimePrompt(
      tasks,
      updatedDefinitions,
      nodeItemsConfig
    );

    setPrompt(generatedPrompt);
  }, [tasks, itemType]);

  return prompt;
}

/**
 * Example 5: Generate prompt without example scenario (lighter version)
 */
export function generateLightweightPrompt(tasks) {
  return generateRuntimePrompt(
    tasks,
    nodeDefinitions,
    nodeItemsConfig,
    null // Don't include example scenario to reduce token usage
  );
}

/**
 * Example 6: Integration with scenario editor
 */
export function ScenarioEditorWithLLM({ tasks, onScenarioGenerated }) {
  const [userPrompt, setUserPrompt] = React.useState('');
  const [generating, setGenerating] = React.useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const systemPrompt = generateRuntimePrompt(
        tasks,
        nodeDefinitions,
        nodeItemsConfig,
        testScenario
      );

      // Call your LLM API
      // In a real implementation, you would pass systemPrompt to your LLM API
      // Example: const response = await callLLMAPI(systemPrompt, userPrompt);
      console.log('System prompt:', systemPrompt);
      const response = () => {
        return {
          scenario: testScenario,
        };
      };

      // Parse the generated scenario
      const generatedScenario = JSON.parse(response);

      // Validate it matches schema
      const isValid = true;

      if (isValid) {
        onScenarioGenerated(generatedScenario);
      } else {
        console.error('Generated scenario is invalid');
      }
    } catch (error) {
      console.error('Error generating scenario:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <textarea
        value={userPrompt}
        onChange={(e) => setUserPrompt(e.target.value)}
        placeholder="Describe the scenario you want to create..."
      />
      <button onClick={handleGenerate} disabled={generating}>
        {generating ? 'Generating...' : 'Generate Scenario'}
      </button>
    </div>
  );
}

/**
 * Example 7: Export prompt to file for external LLM usage
 */
export function exportPromptToFile(tasks, filename = 'scenario-llm-prompt.txt') {
  const prompt = generateRuntimePrompt(
    tasks,
    nodeDefinitions,
    nodeItemsConfig,
    testScenario
  );

  const blob = new Blob([prompt], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Example 8: Console helper for debugging
 */
export function logPromptToConsole(tasks, nodesConfig, nodesDefinitions) {
  const prompt = generateRuntimePrompt(
    tasks,
    nodesDefinitions,
    nodesConfig,
    testScenario
  );

  console.log('='.repeat(80));
  console.log('SCENARIO LLM PROMPT');
  console.log('='.repeat(80));
  console.log(JSON.stringify(prompt));
  console.log('='.repeat(80));
  console.log(`Prompt length: ${prompt.length} characters`);
  console.log(`Estimated tokens: ~${Math.ceil(prompt.length / 4)}`);
}

/**
 * Example 9: Generate patches with automatic retry on format errors
 * This is the RECOMMENDED way to use patch mode
 */
export async function generateScenarioPatchesWithRetry(
  userRequest,
  currentScenario,
  tasks,
  apiKey
) {
  // Generate the system prompt with patch mode
  const systemPrompt = generateRuntimePrompt(
    tasks,
    nodeDefinitions,
    nodeItemsConfig,
    currentScenario // Pass current scenario for context
  );

  // Use the retry mechanism to ensure proper format
  const result = await generatePatchesWithRetry(
    systemPrompt,
    userRequest,
    apiKey,
    2 // Max 2 retries
  );

  if (result.success) {
    console.log(`✅ Successfully generated patches in ${result.attempts} attempt(s)`);
    if (result.warning || result.validationWarning) {
      console.warn('⚠️ Warning:', result.warning || result.validationWarning);
    }
    return {
      explanation: result.explanation,
      patches: result.patches,
      notes: result.notes,
    };
  } else {
    console.error('❌ Failed to generate patches:', result.error);
    throw new Error(result.error);
  }
}

/**
 * Example 10: Quick validation check without making API calls
 * Use this to validate LLM responses before parsing
 */
export function checkResponseFormat(responseText, mode = 'patch') {
  if (mode === 'patch') {
    const validation = validatePatchResponse(responseText);

    if (validation.isValid) {
      console.log('✅ Response format is valid');
      return true;
    } else {
      console.error('❌ Response format is invalid:');
      validation.issues.forEach(issue => console.error('  -', issue));
      return false;
    }
  } else {
    // Validate payload mode
    const hasStart = responseText.includes('<<<SCENARIO_PAYLOAD>>>');
    const hasEnd = responseText.includes('<<<END_PAYLOAD>>>');

    if (hasStart && hasEnd) {
      console.log('✅ Response format is valid');
      return true;
    } else {
      console.error('❌ Response format is invalid:');
      if (!hasStart) console.error('  - Missing <<<SCENARIO_PAYLOAD>>> marker');
      if (!hasEnd) console.error('  - Missing <<<END_PAYLOAD>>> marker');
      return false;
    }
  }
}

/**
 * Example 11: Complete workflow - Generate patches and apply them to scenario
 */
export async function modifyScenarioWithLLM(
  userRequest,
  currentScenario,
  tasks,
  apiKey,
  options = {}
) {
  const { autoRepair = true } = options;

  console.log('🚀 Starting LLM-based scenario modification...');

  // Step 1: Generate patches with retry mechanism
  const patchResult = await generateScenarioPatchesWithRetry(
    userRequest,
    currentScenario,
    tasks,
    apiKey
  );

  if (!patchResult.patches) {
    throw new Error('Failed to generate patches from LLM');
  }

  console.log('📝 LLM Explanation:', patchResult.explanation);
  console.log('🔧 Patches:', patchResult.patches);

  // Step 2: Apply patches to scenario
  const applyResult = applyPatchesToScenario(currentScenario, patchResult.patches);

  if (!applyResult.success) {
    console.error('❌ Patch application had errors:', applyResult.changes.errors);
  }

  let finalScenario = applyResult.scenario;
  let repairResult = null;

  // Step 3: Auto-repair broken links if enabled
  if (autoRepair) {
    const validation = validateScenarioStructure(finalScenario);

    if (!validation.isValid) {
      console.warn('⚠️ Scenario has validation errors, attempting auto-repair...');
      repairResult = repairScenarioBidirectionalLinks(finalScenario);
      finalScenario = repairResult.scenario;

      if (repairResult.repairCount > 0) {
        console.log(`✅ Auto-repair applied ${repairResult.repairCount} fixes`);
      }
    }
  }

  // Step 4: Final validation
  const finalValidation = validateScenarioStructure(finalScenario);

  if (!finalValidation.isValid) {
    console.error('❌ Modified scenario still has structural errors:', finalValidation.errors);
  }

  if (finalValidation.warnings.length > 0) {
    console.warn('⚠️ Modified scenario has warnings:', finalValidation.warnings);
  }

  // Step 5: Return complete result
  return {
    scenario: finalScenario,
    explanation: patchResult.explanation,
    notes: patchResult.notes,
    changes: applyResult.changes,
    repairs: repairResult ? repairResult.repairs : [],
    validation: finalValidation,
    success: applyResult.success && finalValidation.isValid,
  };
}

/**
 * Example 12: Apply patches manually (without LLM)
 */
export function applyManualPatches(currentScenario, patches) {
  // Apply patches
  const result = applyPatchesToScenario(currentScenario, patches);

  // Validate
  const validation = validateScenarioStructure(result.scenario);

  console.log('Summary:');
  console.log('  Added:', result.changes.added.length, 'nodes');
  console.log('  Updated:', result.changes.updated.length, 'nodes');
  console.log('  Removed:', result.changes.removed.length, 'nodes');
  console.log('  Errors:', result.changes.errors.length);
  console.log('  Valid:', validation.isValid);

  return {
    scenario: result.scenario,
    changes: result.changes,
    validation,
    success: result.success && validation.isValid,
  };
}

/**
 * Example 13: Test patch application with sample data
 */
export function testPatchApplication() {
  // Sample scenario with 3 nodes
  const sampleScenario = {
    'start-1': {
      id: 'start-1',
      type: 'StartNode',
      branchId: '1',
      data: { label: 'Play' },
      nextNodes: ['node-1'],
      prevNodes: [],
    },
    'node-1': {
      id: 'node-1',
      type: 'GenericNode',
      branchId: '1',
      parentNodeId: 'start-1',
      data: {
        label: 'Welcome',
        nodeType: 'message:info',
        message: 'Hello!',
      },
      nextNodes: ['finish-1'],
      prevNodes: ['start-1'],
    },
    'finish-1': {
      id: 'finish-1',
      type: 'FinishNode',
      branchId: '1',
      data: { label: 'Finish' },
      nextNodes: [],
      prevNodes: ['node-1'],
    },
  };

  // Sample patches
  const samplePatches = {
    add: {
      'new-node-1': {
        id: 'new-node-1',
        type: 'GenericNode',
        branchId: '1',
        parentNodeId: 'node-1',
        data: {
          label: 'Question',
          nodeType: 'message:single',
          message: 'How are you?',
          response: [
            { label: 'Good', value: 'good' },
            { label: 'Bad', value: 'bad' },
          ],
        },
        nextNodes: ['finish-1'],
        prevNodes: ['node-1'],
      },
    },
    update: {
      'node-1': {
        set: {
          'data.message': 'Welcome to the updated scenario!',
        },
        links: {
          nextNodes: ['new-node-1'],
        },
      },
      'finish-1': {
        links: {
          prevNodes: ['new-node-1'],
        },
      },
    },
  };

  console.log('🧪 Testing patch application...');
  console.log('Original scenario:', Object.keys(sampleScenario));

  const result = applyManualPatches(sampleScenario, samplePatches);

  console.log('Modified scenario:', Object.keys(result.scenario));
  console.log('Success:', result.success);

  return result;
}

/**
 * Example 14: React hook for LLM-based scenario modification
 */
export function useScenarioLLMModification(apiKey) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [lastResult, setLastResult] = React.useState(null);

  const modifyScenario = React.useCallback(
    async (userRequest, currentScenario, tasks, options = {}) => {
      setLoading(true);
      setError(null);

      try {
        const result = await modifyScenarioWithLLM(
          userRequest,
          currentScenario,
          tasks,
          apiKey,
          options
        );

        setLastResult(result);

        if (!result.success) {
          setError(
            result.validation.errors.length > 0
              ? `Validation errors: ${result.validation.errors.join(', ')}`
              : `Application errors: ${result.changes.errors.join(', ')}`
          );
        }

        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiKey]
  );

  return {
    modifyScenario,
    loading,
    error,
    lastResult,
  };
}

/**
 * Example 15: Repair broken scenario links (standalone)
 * Use this when you have a scenario with broken links from any source
 */
export function repairBrokenScenario(brokenScenario) {
  console.log('🔍 Checking scenario for broken links...');

  // First validate to see what's broken
  const validation = validateScenarioStructure(brokenScenario);

  if (validation.isValid) {
    console.log('✅ Scenario is already valid, no repairs needed');
    return {
      scenario: brokenScenario,
      repairs: [],
      removed: [],
      wasValid: true,
    };
  }

  console.log('❌ Found validation errors:', validation.errors);
  console.log('⚠️ Found warnings:', validation.warnings);

  // Repair the scenario
  const repairResult = repairScenarioBidirectionalLinks(brokenScenario);

  // Validate again
  const finalValidation = validateScenarioStructure(repairResult.scenario);

  if (finalValidation.isValid) {
    console.log('✅ Scenario repaired successfully!');
  } else {
    console.error('❌ Scenario still has errors after repair:', finalValidation.errors);
  }

  return {
    scenario: repairResult.scenario,
    repairs: repairResult.repairs,
    removed: repairResult.removed,
    wasValid: false,
    isValidNow: finalValidation.isValid,
    remainingErrors: finalValidation.errors,
    warnings: finalValidation.warnings,
  };
}
